import { Suspense, useEffect, useState } from "react";
import { DataGrid, GridCol } from "@/components/DataGrid";
import { ToneCell, BulkToneBar } from "@/components/ToneTools";
import { useSearchParams } from "@/lib/next-compat";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Drawer } from "@/pages/a/monitoring/articles/detail";
import { Highlight } from "@/components/Highlight";
import { Ch } from "@/components/Fa";

const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };

const ALL = "twitter,instagram,facebook,tiktok,youtube,blog,forum,other";
const CH = [[ALL, "Semua"], ["twitter", "X/Twitter"], ["instagram", "Instagram"], ["facebook", "Facebook"], ["tiktok", "TikTok"], ["youtube", "YouTube"], ["blog,forum,other", "Blog/Forum"]] as const;
const PT = [["", "Semua"], ["parent", "Post Induk"], ["reply", "Reply/Komentar"]] as const;
const SENT = [["", "Semua tone"], ["positive", "Positif"], ["neutral", "Netral"], ["negative", "Negatif"]] as const;


export default function SocialPostsPage() { return <Suspense><Inner /></Suspense>; }
function Inner() {
  const sp = useSearchParams();
  const [rows, setRows] = useState<any[]>([]);
  const [ch, setCh] = useState<string>(sp.get("sourceType") ?? ALL);
  const [pt, setPt] = useState<string>(sp.get("postType") ?? "");
  const [sent, setSent] = useState<string>(sp.get("sentiment") ?? "");
  const [author, setAuthor] = useState<string>(sp.get("author") ?? "");
  const [thread, setThread] = useState<string>(sp.get("thread") ?? "");
  const [label, setLabel] = useState<string>(sp.get("label") ?? "");
  const [labels, setLabels] = useState<Array<{ id: string; name: string; type: string; count: number }>>([]);
  const labelName = sp.get("labelName");
  const [q, setQ] = useState(sp.get("q") ?? "");
  const [subj, setSubj] = useState<string>(sp.get("subject") ?? "");
  const [subjOpts, setSubjOpts] = useState<{ id: string; name: string }[]>([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<{ total: number; totalPages: number }>({ total: 0, totalPages: 1 });
  const [detailId, setDetailId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState(sp.get("sort") ?? "");
  const [sortDir, setSortDir] = useState(sp.get("dir") ?? "desc");
  const [editTone, setEditTone] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const pageIds = rows.map((r: any) => r.id);
  const allSel = pageIds.length > 0 && pageIds.every((i2: string) => sel.has(i2));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(pageIds));
  const toggleSel = (id: string) => setSel((p2) => { const n = new Set(p2); n.has(id) ? n.delete(id) : n.add(id); return n; });
  function clickSort(k: string) {
    const nd = sortKey === k ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortKey(k); setSortDir(nd); setPage(1);
    load({ page: 1, sortKey: k, sortDir: nd });
  }
  const today = new Date().toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 86400e3).toISOString().slice(0, 10); // default: Kemarin (H-1 WIB)
  const [start, setStart] = useState(sp.get("start") ?? defStart);
  const [end, setEnd] = useState(sp.get("end") ?? today);

  async function load(o?: Partial<{ page: number; ch: string; pt: string; sent: string; author: string; thread: string; start: string; end: string; sortKey: string; sortDir: string; subject: string; label: string }>) {
    const P = o?.page ?? page;
    const p = new URLSearchParams({ sourceType: o?.ch ?? ch, start: o?.start ?? start, end: o?.end ?? end, page: String(P), pageSize: "50" });
    if (q) p.set("q", q);
    const SB = o && "subject" in o ? (o as any).subject : subj; if (SB) p.set("subject", SB);
    const sk = o?.sortKey ?? sortKey, sd = o?.sortDir ?? sortDir;
    if (sk) { p.set("sort", sk); p.set("dir", sd); }
    const PT2 = o?.pt ?? pt; if (PT2) p.set("postType", PT2);
    const SN = o?.sent ?? sent; if (SN) p.set("sentiment", SN);
    const A = o?.author ?? author; if (A) p.set("author", A);
    const T = o?.thread ?? thread; if (T) p.set("thread", T);
    const LB = o && "label" in o ? o.label : label; if (LB) p.set("label", LB);
    const r = await fetch(`/api/v1/mentions?${p}`);
    if (r.status === 401) { location.href = "/login"; return; }
    const j = await r.json();
    setRows(j.data ?? []); setMeta({ total: j.meta?.total ?? 0, totalPages: j.meta?.totalPages ?? 1 });
  }
  useEffect(() => { load({ page: 1 }); }, []);
  useEffect(() => {
    fetch("/api/v1/subjects/scopes").then((r) => (r.ok ? r.json() : { data: [] })).then((j) => {
      const opts = (j.data ?? []).filter((s: any) => (s.scopes ?? []).some((sc: any) => sc.vendor === "neticle")).map((s: any) => ({ id: s.id, name: s.name }));
      setSubjOpts(opts);
    }).catch(() => {});
    fetch(`/api/v1/labels/list?wing=social&start=${start}&end=${end}`).then((r) => (r.ok ? r.json() : { data: [] })).then((j) => setLabels(j.data ?? [])).catch(() => {});
  }, []);
  const go = (P: number) => { const c = Math.min(Math.max(1, P), meta.totalPages); setPage(c); load({ page: c }); };
  const n = (x: number | null | undefined) => (x == null ? "-" : Intl.NumberFormat("id-ID", { notation: "compact" }).format(x));
  const clearChip = (k: "author" | "thread") => { if (k === "author") setAuthor(""); else setThread(""); setPage(1); load({ page: 1, [k]: "" } as any); };

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Post List</h1>
          <div className="avio-t-sm avio-muted">Social · Neticle · {start} → {end} (WIB)</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); setPage(1); load({ page: 1, start: s2, end: e2 }); }} />
        </div>
      </div>
      <SyncBar wing="social" />
      {(author || thread || label) && (
        <p style={{ margin: "0 0 var(--space-5)", display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
          {author && <span className="avio-chip" data-shape="pill" style={{ background: "var(--color-action-soft)", color: "var(--brand-700)" }}><i className="fa-solid fa-user" /> @{author} <a onClick={() => clearChip("author")} style={{ color: "inherit", marginLeft: "var(--space-2)", cursor: "pointer" }}><i className="fa-solid fa-xmark" /></a></span>}
          {label && <span className="avio-chip" data-shape="pill" style={{ background: "var(--status-warn-bg)", color: "var(--status-warn)" }}><i className="fa-solid fa-tag" /> {labelName ?? "label"} <a href="/a/social/posts" style={{ color: "inherit", marginLeft: "var(--space-2)" }}><i className="fa-solid fa-xmark" /></a></span>}
          {thread && <span className="avio-chip" data-shape="pill" style={{ background: "var(--color-action-soft)", color: "var(--brand-700)" }}><i className="fa-solid fa-comments" /> thread {thread} <a onClick={() => clearChip("thread")} style={{ color: "inherit", marginLeft: "var(--space-2)", cursor: "pointer" }}><i className="fa-solid fa-xmark" /></a></span>}
        </p>
      )}
      <div>
        <div className="avio-toolbar">
        <div className="avio-toolbar__row">
        <select className="avio-select" value={subj} onChange={(e) => { const v = e.target.value; setSubj(v); setPage(1); load({ page: 1, subject: v }); }} title="Filter per keyword (subjek pantauan Neticle)" style={{ minWidth: 150, maxWidth: 220 }}>
          <option value="">Semua keyword</option>
          {subjOpts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select className="avio-select" value={label} onChange={(e) => { const v = e.target.value; setLabel(v); setPage(1); load({ page: 1, label: v }); }} title="Filter berdasarkan label AI" style={{ minWidth: 160, maxWidth: 220, display: labels.length ? undefined : "none" }}>
          <option value="">Semua label</option>
          {Object.entries(labels.reduce((acc: Record<string, typeof labels>, l) => { (acc[l.type] ??= []).push(l); return acc; }, {})).map(([type, items]) => (
            <optgroup key={type} label={type}>
              {items.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.count})</option>)}
            </optgroup>
          ))}
        </select>
        <label className="avio-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input className="avio-input" placeholder="Cari isi percakapan..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load({ page: 1 }))} />
        </label>
        <button className="avio-btn" data-variant="primary" onClick={() => { setPage(1); load({ page: 1 }); }}><i className="fa-solid fa-filter" /> Terapkan</button>
        <a className="avio-btn" data-variant="secondary" href={`/api/v1/exports/medsos?start=${start}&end=${end}${sent ? `&sentiment=${sent}` : ""}`}><i className="fa-solid fa-file-excel" /> Export</a>
      </div>
      <div className="avio-toolbar__row">
        <div className="avio-segment">
          {CH.map(([val, label]) => (
            <button key={val} className="avio-segment__item" aria-pressed={ch === val} onClick={() => { setCh(val); setPage(1); load({ page: 1, ch: val }); }}>{label}</button>
          ))}
        </div>
        <div className="avio-segment">
          {PT.map(([val, label]) => (
            <button key={val} className="avio-segment__item" aria-pressed={pt === val} onClick={() => { setPt(val); setPage(1); load({ page: 1, pt: val }); }}>{label}</button>
          ))}
        </div>
        <div className="avio-segment">
          {SENT.map(([val, label]) => (
            <button key={"s" + val} className="avio-segment__item" aria-pressed={sent === val} onClick={() => { setSent(val); setPage(1); load({ page: 1, sent: val }); }}>{label}</button>
          ))}
        </div>
        <button className="avio-btn" data-variant={editTone ? "primary" : "secondary"} data-size="sm" onClick={() => { setEditTone(!editTone); setSel(new Set()); }}>
          <i className={editTone ? "fa-solid fa-check" : "fa-solid fa-user-pen"} /> {editTone ? "Selesai koreksi" : "Koreksi sentimen"}
        </button>
        <span className="avio-count" style={{ marginLeft: "auto" }}><b>{meta.total.toLocaleString("id-ID")}</b> post</span>
      </div>
      {editTone && (
        <div className="avio-banner" data-tone="info" style={{ margin: 0 }}>
          <i className="fa-solid fa-user-pen avio-banner__icon" />
          <div>Mode koreksi aktif — ubah tone per baris, atau centang beberapa baris lalu ubah massal. Koreksi analis kebal ditimpa sync berikutnya.</div>
        </div>
      )}
      {editTone && sel.size > 0 && <BulkToneBar selected={[...sel]} onClear={() => setSel(new Set())} onDone={() => { setSel(new Set()); load(); }} />}
      </div>
      <div className="avio-tablewrap">
      <DataGrid
        cols={[
          ...(editTone ? [{ id: "sel", header: <input type="checkbox" checked={allSel} onChange={toggleAll} onClick={(e: any) => e.stopPropagation()} title="Pilih semua di halaman ini" style={{ cursor: "pointer" }} />, width: "max", cell: (r: any) => <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }} /> }] : []),
          { id: "date", header: "Tanggal (WIB)", width: "max", sortable: true, cell: (r: any) => new Date(new Date(r.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(0, 16).replace("T", " ") },
          { id: "kanal", header: "Kanal", width: "max", cell: (r: any) => <span><Ch t={r.sourceType} /> {r.sourceType}</span> },
          { id: "tipe", header: "Tipe", width: "max", cell: (r: any) => r.isReply
            ? <span title="Reply/komentar — klik utk lihat 1 thread" onClick={(e) => { e.stopPropagation(); if (r.threadId) { setThread(String(r.threadId)); setPage(1); load({ page: 1, thread: String(r.threadId) }); } }} style={{ color: "var(--color-text-secondary)" }}><i className="fa-solid fa-reply" /> reply</span>
            : <span title="Post induk — klik utk lihat 1 thread" onClick={(e) => { e.stopPropagation(); if (r.threadId) { setThread(String(r.threadId)); setPage(1); load({ page: 1, thread: String(r.threadId) }); } }} style={{ color: "var(--brand-700)" }}><i className="fa-solid fa-comment-dots" /> post</span> },
          { id: "author", header: "Author", width: "max", cell: (r: any) => r.authorHandle
            ? <a onClick={(e) => { e.stopPropagation(); setAuthor(r.authorHandle); setPage(1); load({ page: 1, author: r.authorHandle }); }} title="Filter post author ini" style={{ color: "var(--color-text-link)", cursor: "pointer" }}>@{r.authorHandle}</a> : "" },
          { id: "konten", header: "Konten (klik = detail)", width: "min:260", cell: (r: any) => <span>
              <Highlight text={((r.title || r.content || "").replace(/\s+/g, " ").trim()).slice(0, 170) || "(tanpa konten)"} extra={[q]} />
              {r.url && <a href={r.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ marginLeft: 6 }}>↗</a>}
            </span> },
          { id: "tone", header: "Tone", width: "max", sortable: true, cell: (r: any) => editTone
              ? <ToneCell id={r.id} value={r.sentimentLabel} provenance={r.sentimentProvenance} onSaved={() => load()} />
              : r.sentimentLabel
                ? <span className="avio-chip" data-sentiment={r.sentimentLabel}><span className="avio-chip__label">{TONE_ID[r.sentimentLabel] ?? r.sentimentLabel}</span>{r.sentimentProvenance === "human_override" && <i className="fa-solid fa-user-pen" title="Koreksi analis" style={{ marginLeft: "var(--space-2)", fontSize: 9 }} />}</span>
                : <span className="avio-muted">—</span> },
          { id: "likes", header: "Likes", width: "max", sortable: true, align: "right", cell: (r: any) => n(r.likes) },
          { id: "comments", header: "Cmt", width: "max", sortable: true, align: "right", cell: (r: any) => n(r.comments) },
          { id: "shares", header: "Share", width: "max", sortable: true, align: "right", cell: (r: any) => n(r.shares) },
          { id: "total", header: "Total", width: "max", sortable: true, align: "right", cell: (r: any) => <b>{n(r.interactions ?? ((r.likes ?? 0) + (r.comments ?? 0) + (r.shares ?? 0) || null))}</b> },
          { id: "reach", header: "Reach", width: "max", sortable: true, align: "right", cell: (r: any) => n(r.reach) },
        ] as GridCol<any>[]}
        data={rows}
        sortMode="server"
        serverSort={sortKey ? { key: sortKey, dir: sortDir as "asc" | "desc" } : null}
        onServerSort={clickSort}
        onRowClick={(r) => setDetailId(r.id)}
        emptyText="Belum ada post utk filter ini."
        minTableWidth={1050}
      />
      </div>
      </div>
      <div className="avio-pagination" style={{ justifyContent: "center" }}>
        <button className="avio-page-btn" onClick={() => go(1)} disabled={page === 1} title="Halaman pertama">«</button>
        <button className="avio-page-btn" onClick={() => go(page - 1)} disabled={page === 1}>‹</button>
        <span className="avio-sub">hal. {page} dari {meta.totalPages}</span>
        <button className="avio-page-btn" onClick={() => go(page + 1)} disabled={page >= meta.totalPages}>›</button>
        <button className="avio-page-btn" onClick={() => go(meta.totalPages)} disabled={page >= meta.totalPages} title="Halaman terakhir">»</button>
      </div>
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
